import type { BillingAddress } from "~/types/Payment";

export const INTERCOM_PURCHASE_TRACK_DELAY_MS = 3000;

/** The only country Inner Balance ships to, as an ISO 3166-1 alpha-2 code. */
export const US_COUNTRY_CODE = "US";

export const TAX_AMOUNT = 0;

/**
 * Shown when a submit is blocked because the cart itself is gone — no items at
 * all, which is what a corrupted `localStorage` looks like from the submit.
 *
 * Deliberately not the generic "try again later" the payment failures use:
 * nothing arrives later, and re-adding the product is what clears it. Only
 * correct when the cart is genuinely empty — see `CART_NOT_READY_MESSAGE` for
 * the other half.
 */
export const CART_EMPTY_MESSAGE =
    "Your cart is empty. Please add your product again and retry.";

/**
 * Shown when the cart has items but none of them has resolved to a price item
 * yet — the product request is still in flight, or it failed outright.
 *
 * Split out from `CART_EMPTY_MESSAGE` because that one told a customer looking
 * at a full cart that it was empty, and asked them to re-add a product they
 * could see was already there. The submit guard cannot tell the two apart from
 * `priceItems` alone (it is derived from `selectedPriceItem?.slug`, so an
 * unresolved item is indistinguishable from an absent one), which is exactly
 * why the `checkout_submit_blocked` record raised there carries
 * `cartItemCount` — this uses the same number.
 *
 * Says refresh rather than wait, because the guard cannot tell a slow request
 * from a dead one either. "Wait a moment" is a lie in the failed case and
 * loops the customer forever; a refresh re-runs the request and so is the one
 * instruction that is true of both.
 */
export const CART_NOT_READY_MESSAGE =
    "Your order isn't ready yet. Please refresh the page and try again.";

/**
 * What the checkout tells a customer when a payment did not go through.
 *
 * One home for all of them because they were declared six times across five
 * hooks, three of them byte-identical, and a copy change had to find every
 * copy. They stay distinct strings rather than collapsing into one: each names
 * a different thing to do next, and that is the only useful part of an error
 * message.
 */

/**
 * The payment was attempted and failed for a reason we cannot name. Used by
 * the guest and authenticated card flows and by a 422 that arrived with no
 * wording of its own.
 */
export const PAYMENT_ERROR_MESSAGE =
    "Unable to process your payment. Please try again later.";

/**
 * The same conclusion on the Thank You upsell, which has its own wording
 * because the customer has already paid once and is not stuck on a checkout.
 */
export const UPSELL_PAYMENT_ERROR_MESSAGE =
    "Something went wrong. Please try again later.";

/**
 * A saved-card checkout rendered with no card to charge. Not a retry message:
 * nothing changes until a payment method is added.
 */
export const MISSING_PAYMENT_SOURCE_MESSAGE =
    "Add your payment details to complete the purchase.";

/** An authenticated submit that failed past the point of self-service. */
export const GENERIC_SUBMIT_ERROR =
    "We couldn't process your payment. Please try again or contact support.";

/**
 * Shown when the payment form never came up. Not the card-error wording, which
 * would send the customer round a form that cannot submit whatever they type.
 */
export const PAYMENT_UNAVAILABLE_MESSAGE =
    "We could not start the payment. Please refresh the page and try again.";

/**
 * Shown when a wallet payment failed and we have nothing better to say — the
 * gateway sent no cardholder-facing reason, or the failure never reached it.
 * When the gateway DID name a reason ("Your card has insufficient funds.") that
 * reason is shown instead: this copy sends the customer back to the same card
 * with no idea what to change.
 */
export const PAYMENT_RETRY_ERROR = "Payment processing error. Please try again.";

/**
 * The card itself is what needs attention — the gateway refused it and told us
 * nothing usable, or the tokenize came back empty.
 *
 * One string for both, which previously differed only by a colon where the
 * other had a full stop. Nothing distinguished them but the punctuation, and a
 * copy change would have had to find both.
 */
export const CARD_DETAILS_ERROR_MESSAGE =
    "Card error. Please check your card details and try again.";

/**
 * Every billing field blanked — the store shape for "no separate billing
 * address". Setting the fields to empty strings rather than dropping them is
 * what keeps `hasBillingAddressData` false, so the billing form collapses and
 * hidden values cannot leak into a submission.
 */
export const EMPTY_BILLING_ADDRESS: BillingAddress = {
    fullName: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    phone: "",
};
